import { useEffect, useMemo, useRef, useState } from "react";
import type { CountryCode } from "libphonenumber-js";
import { getCountryOptions } from "./phoneUtils";
import "./phone.css";

// "+91 v" button with the country list (dial code + name) underneath.
// Typing a letter while the list is open jumps to the first country with it.

interface CountryCodeSelectProps {
    value: CountryCode;
    onChange: (code: CountryCode) => void;
    disabled?: boolean;
}

const CountryCodeSelect = ({ value, onChange, disabled }: CountryCodeSelectProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const options = useMemo(getCountryOptions, []);
    const selected = options.find((o) => o.code === value);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
                return;
            }
            if (e.key.length === 1 && /[a-z]/i.test(e.key)) {
                const index = options.findIndex((o) => o.name.toLowerCase().startsWith(e.key.toLowerCase()));
                const row = index >= 0 ? listRef.current?.children[index] as HTMLElement | undefined : undefined;
                row?.scrollIntoView({ block: "nearest" });
                row?.focus();
            }
        };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        // Show the selected country when the list opens.
        const index = options.findIndex((o) => o.code === value);
        (listRef.current?.children[index] as HTMLElement | undefined)?.scrollIntoView({ block: "center" });
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, options, value]);

    return (
        <div className="pv-country" ref={rootRef}>
            <button
                type="button"
                className="pv-country-btn"
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Country code ${selected?.dialCode || ""} ${selected?.name || ""}`}
            >
                <span>{selected?.dialCode || "+"}</span>
                <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
                    <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <ul className="pv-country-list" role="listbox" ref={listRef}>
                    {options.map((o) => (
                        <li
                            key={o.code}
                            role="option"
                            aria-selected={o.code === value}
                            tabIndex={-1}
                            className={o.code === value ? "is-selected" : ""}
                            onClick={() => {
                                onChange(o.code);
                                setOpen(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    onChange(o.code);
                                    setOpen(false);
                                }
                            }}
                        >
                            <span className="pv-country-dial">{o.dialCode}</span>
                            <span className="pv-country-name">{o.name}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CountryCodeSelect;
